/**
 * KORERIG cart drawer upsells.
 *
 * De upsell-markup wordt server-side door Shopify gerenderd in de sectie
 * kr-cart-upsells en staat daar in een <template>. Dit script plaatst die
 * markup in de cart drawer, onder de cart items, en houdt hem in sync als de
 * drawer opnieuw rendert.
 *
 * Toevoegen gaat via de Shopify Ajax Cart API. Daarna laat de drawer zichzelf
 * opnieuw renderen vanuit Shopify, zodat kortingen, subtotaal en totaal altijd
 * door Shopify berekend blijven.
 *
 * Alle listeners staan op document, zodat een sectie-rerender geen dubbele
 * event listeners oplevert.
 */

import { ThemeEvents, CartUpdateEvent, CartErrorEvent } from '@theme/events';

/**
 * Het theme declareert `Theme` met const in een klassiek script. Zo'n binding
 * komt in de globale lexicale scope en dus NIET op window. Bare `Theme` werkt
 * wel, maar gooit als het script ontbreekt — vandaar de typeof-guard.
 * @returns {any}
 */
function themeGlobal() {
  // eslint-disable-next-line no-undef
  if (typeof Theme !== 'undefined') return Theme;
  return window.Theme || null;
}

/** @returns {Promise<any|null>} */
async function fetchCart() {
  try {
    return await (await fetch(`${themeGlobal()?.routes?.cart_url || '/cart'}.js`)).json();
  } catch {
    return null;
  }
}

/**
 * Zet de upsells in de drawer. Producten die al in de cart liggen worden
 * overgeslagen en er wordt nooit meer getoond dan het ingestelde maximum.
 */
async function syncUpsells() {
  const dialog = document.querySelector('cart-drawer-component dialog');
  if (!dialog) return;

  const content = dialog.querySelector('.cart-drawer__content');
  const summary = dialog.querySelector('.cart-drawer__summary');
  if (!content || !summary) return;

  // Een server-side gerenderde upsellblok in de drawer wint altijd.
  if (content.querySelector('.kr-upsell:not([data-kr-injected])')) return;

  for (const stale of content.querySelectorAll('[data-kr-injected]')) stale.remove();

  const template = document.getElementById('kr-upsell-template');
  if (!(template instanceof HTMLTemplateElement)) return;

  const cart = await fetchCart();
  const inCart = new Set((cart?.items || []).map((item) => String(item.product_id)));

  const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
  const block = fragment.querySelector('[data-kr-upsell-block]');
  if (!(block instanceof HTMLElement)) return;

  block.setAttribute('data-kr-injected', '');

  const max = Number(template.dataset.krUpsellMax || 2);
  let shown = 0;

  for (const item of [...block.querySelectorAll('[data-kr-upsell]')]) {
    const productId = String(/** @type {HTMLElement} */ (item).dataset.krUpsellProduct || '');
    if (shown >= max || inCart.has(productId)) {
      item.remove();
      continue;
    }
    shown += 1;
  }

  if (shown === 0) return;

  summary.parentNode?.insertBefore(block, summary);
}

if (!window.__krCartUpsellBound) {
  window.__krCartUpsellBound = true;

  // Prijs meelopen met de gekozen variant.
  document.addEventListener('change', (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-kr-upsell-variant]')) return;

    const item = select.closest('[data-kr-upsell]');
    if (!item) return;

    const option = select.selectedOptions[0];
    const priceSlot = item.querySelector('[data-kr-upsell-price]');
    if (priceSlot && option?.dataset.price) priceSlot.textContent = option.dataset.price;

    const button = item.querySelector('[data-kr-upsell-add]');
    if (button instanceof HTMLElement) button.dataset.krUpsellAdd = select.value;
  });

  document.addEventListener('click', async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target?.closest?.('[data-kr-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();

    const item = button.closest('[data-kr-upsell]');
    const select = item?.querySelector('[data-kr-upsell-variant]');
    const variantId =
      select instanceof HTMLSelectElement ? select.value : String(button.dataset.krUpsellAdd || '');

    if (!variantId) return;

    const originalLabel = button.textContent;
    button.disabled = true;

    // Vraag Shopify om de cart-secties opnieuw te renderen.
    const sectionIds = new Set();
    for (const node of document.querySelectorAll('cart-items-component')) {
      if (node instanceof HTMLElement && node.dataset.sectionId) sectionIds.add(node.dataset.sectionId);
    }

    try {
      const response = await fetch(themeGlobal()?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: [...sectionIds].join(','),
          sections_url: window.location.pathname,
        }),
      });

      const payload = await response.json();

      if (payload.status) {
        document.dispatchEvent(
          new CartErrorEvent('kr-cart-upsell', payload.message, payload.description, payload.errors)
        );
        button.textContent = originalLabel;
        button.disabled = false;
        return;
      }

      const cart = await fetchCart();

      // De drawer luistert op document en morpht zichzelf met de verse secties.
      document.dispatchEvent(
        new CartUpdateEvent(cart ?? {}, 'kr-cart-upsell', {
          source: 'kr-cart-upsell',
          itemCount: cart?.item_count,
          variantId,
          sections: payload.sections,
        })
      );
    } catch (error) {
      console.error('[KORERIG] upsell add failed', error);
      button.disabled = false;
      button.textContent = originalLabel;
    }
  });

  // De drawer wordt bij elke cartwijziging opnieuw gerenderd; daarna opnieuw
  // plaatsen. Een korte vertraging laat de morph eerst afronden.
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    setTimeout(syncUpsells, 200);
  });

  document.addEventListener('dialog:open', () => {
    setTimeout(syncUpsells, 60);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncUpsells());
  } else {
    syncUpsells();
  }
}
